import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewAPI } from '../services/api';
import { BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Editor from '@monaco-editor/react';

function InterviewDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interview, setInterview] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [regeneratingAI, setRegeneratingAI] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const videoRef = React.useRef(null);

  const isAdmin = user?.role === 'admin';

  const handleRegenerateAI = async () => {
    if (!selectedSubmission || !isAdmin) return;
    
    if (!window.confirm('Regenerate AI assessments for all questions in this submission? This will overwrite existing assessments.')) {
      return;
    }

    setRegeneratingAI(true);
    try {
      await interviewAPI.regenerateAI(selectedSubmission._id);
      alert('AI assessment regeneration started! This may take a few minutes. Please refresh the page in 2-3 minutes to see the updated assessments.');
    } catch (error) {
      console.error('Error regenerating AI:', error);
      alert(error.response?.data?.message || 'Failed to regenerate AI assessments');
    } finally {
      setRegeneratingAI(false);
    }
  };

  useEffect(() => {
    fetchInterviewDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInterviewDetails = async () => {
    setLoading(true);
    try {
      const [interviewRes, submissionsRes] = await Promise.all([
        interviewAPI.getInterview(id),
        interviewAPI.getMySubmissions(id)
      ]);
      
      const interviewData = interviewRes.data.interview;
      
      // Get my status
      const response = await interviewAPI.getMyInterviews('');
      const myInterview = response.data.interviews.find(i => i._id === id);
      
      setInterview({
        ...interviewData,
        myStatus: myInterview?.myStatus || 'not_started',
        attemptNumber: myInterview?.attemptNumber || 1
      });
      
      console.log('Fetched submissions:', submissionsRes.data.submissions);
      console.log('Number of submissions:', submissionsRes.data.submissions?.length);
      
      setSubmissions(submissionsRes.data.submissions || []);
      
      if (submissionsRes.data.submissions && submissionsRes.data.submissions.length > 0) {
        setSelectedSubmission(submissionsRes.data.submissions[0]);
      }
    } catch (error) {
      console.error('Error fetching interview details:', error);
      alert('Failed to load interview details');
      navigate('/my-interviews');
    }
    setLoading(false);
  };

  const getStatusInfo = () => {
    if (!interview) return { text: '', canTake: false };
    
    const now = new Date();
    const startDate = new Date(interview.startDate);
    const endDate = new Date(interview.endDate);
    const attemptNumber = interview.attemptNumber || 1;
    const maxAttempts = interview.maxAttempts || 1;

    if ((interview.myStatus === 'submitted' || interview.myStatus === 'timed_out') && !interview.allowMultipleTakes) {
      return { text: interview.myStatus === 'submitted' ? 'Submitted' : 'Timed Out', canTake: false };
    } else if ((interview.myStatus === 'submitted' || interview.myStatus === 'timed_out') && interview.allowMultipleTakes && now >= startDate && now <= endDate) {
      const canRetake = maxAttempts === 0 || attemptNumber < maxAttempts;
      return { text: canRetake ? 'Retake Available' : 'Max Attempts Reached', canTake: canRetake };
    } else if (now < startDate) {
      return { text: 'Upcoming', canTake: false };
    } else if (now > endDate) {
      return { text: 'Expired', canTake: false };
    } else if (interview.myStatus === 'in_progress') {
      return { text: 'In Progress', canTake: true };
    } else {
      return { text: 'Available', canTake: true };
    }
  };

  const getAnswerForQuestion = (submission, questionId) => {
    if (!submission || !submission.responses || !questionId) return null;
    const qId = questionId._id || questionId;
    return submission.responses.find(r => {
      if (!r || !r.questionId) return false;
      const rqId = r.questionId._id || r.questionId;
      return rqId === qId;
    });
  };
  
  const handleChapterClick = (chapterIndex) => {
    if (videoRef.current && selectedSubmission?.videoChapters) {
      const timestamp = selectedSubmission.videoChapters[chapterIndex].timestamp;
      setCurrentChapter(chapterIndex);
      videoRef.current.currentTime = timestamp;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('Play was prevented:', err);
          });
        }
      }, 100);
    }
  };
  
  const handleVideoTimeUpdate = () => {
    if (!videoRef.current || !selectedSubmission?.videoChapters) return;
    
    const currentTime = videoRef.current.currentTime;
    const chapters = selectedSubmission.videoChapters;
    
    let newChapter = currentChapter;
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (currentTime >= chapters[i].timestamp) {
        newChapter = i;
        break;
      }
    }
    
    if (newChapter !== currentChapter) {
      setCurrentChapter(newChapter);
    }
    
    if (currentChapter < chapters.length - 1) {
      const nextChapterTimestamp = chapters[currentChapter + 1].timestamp;
      if (currentTime >= nextChapterTimestamp) {
        videoRef.current.pause();
      }
    }
  };
  
  const formatChapterTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (startedAt, submittedAt) => {
    if (!startedAt || !submittedAt) return 'N/A';
    
    const start = new Date(startedAt);
    const end = new Date(submittedAt);
    const durationMs = end - start;
    
    if (durationMs < 0) return 'N/A';
    
    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Fetch video URL when submission changes
  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (selectedSubmission && selectedSubmission.videoRecording) {
        setLoadingVideo(true);
        setVideoUrl(null); // Reset video URL
        try {
          const response = await interviewAPI.getResponseVideoUrl(selectedSubmission._id);
          setVideoUrl(response.data.videoUrl);
        } catch (error) {
          console.error('Error fetching video URL:', error);
          // Fallback to direct URL if fetch fails
          setVideoUrl(`${BASE_URL}/${selectedSubmission.videoRecording}`);
        } finally {
          setLoadingVideo(false);
        }
      }
    };

    fetchVideoUrl();
  }, [selectedSubmission]);

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Loading interview details...
          </div>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Interview not found
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate('/my-interviews')}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#667eea',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              marginBottom: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ← Back to My Interviews
          </button>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <h2 style={{ color: '#2d3748', fontSize: '28px', fontWeight: 700, margin: 0 }}>
              {interview.title}
            </h2>
            {statusInfo.canTake && (
              <button
                onClick={() => {
                  setIsNavigating(true);
                  navigate(`/take-interview/${interview._id}`);
                }}
                disabled={isNavigating}
                style={{
                  padding: '12px 24px',
                  background: isNavigating ? '#a0aec0' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: isNavigating ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  opacity: isNavigating ? 0.7 : 1
                }}
              >
                {isNavigating ? 'Loading...' : 
                 interview.myStatus === 'in_progress' ? 'Continue Interview' : 
                 (interview.myStatus === 'submitted' || interview.myStatus === 'timed_out') ? 'Retake Interview' : 
                 'Start Interview'}
              </button>
            )}
          </div>
          
          {interview.description && (
            <p style={{ color: '#718096', fontSize: '15px', lineHeight: 1.6, margin: '12px 0 0 0' }}>
              {interview.description}
            </p>
          )}
        </div>

        {/* Interview Info */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{ 
            background: '#f7fafc', 
            padding: '16px', 
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: 600 }}>
              START DATE
            </div>
            <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
              {new Date(interview.startDate).toLocaleDateString()}
            </div>
            <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
              {new Date(interview.startDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </div>
          </div>

          <div style={{ 
            background: '#f7fafc', 
            padding: '16px', 
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: 600 }}>
              END DATE
            </div>
            <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
              {new Date(interview.endDate).toLocaleDateString()}
            </div>
            <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
              {new Date(interview.endDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </div>
          </div>

          <div style={{ 
            background: '#f7fafc', 
            padding: '16px', 
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: 600 }}>
              DURATION
            </div>
            <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
              {interview.duration} minutes
            </div>
          </div>

          <div style={{ 
            background: '#f7fafc', 
            padding: '16px', 
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: 600 }}>
              QUESTIONS
            </div>
            <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
              {interview.questions?.length || 0}
            </div>
          </div>

          <div style={{ 
            background: '#f7fafc', 
            padding: '16px', 
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: 600 }}>
              STATUS
            </div>
            <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
              {statusInfo.text}
            </div>
          </div>

          {interview.allowMultipleTakes && (
            <div style={{ 
              background: '#f7fafc', 
              padding: '16px', 
              borderRadius: '10px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '6px', fontWeight: 600 }}>
                ATTEMPTS
              </div>
              <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
                {interview.attemptNumber || 1} / {interview.maxAttempts === 0 ? '∞' : interview.maxAttempts}
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        {interview.instructions && (
          <div style={{ 
            background: '#eef2ff', 
            padding: '20px', 
            borderRadius: '10px',
            borderLeft: '4px solid #667eea',
            marginBottom: '32px'
          }}>
            <h4 style={{ color: '#4338ca', fontSize: '14px', fontWeight: 700, margin: '0 0 12px 0' }}>
              📝 Instructions
            </h4>
            <p style={{ color: '#4338ca', fontSize: '14px', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
              {interview.instructions}
            </p>
          </div>
        )}

        {/* Questions List */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ color: '#2d3748', fontSize: '20px', fontWeight: 700, marginBottom: '16px' }}>
            Interview Questions ({interview.questions?.length || 0})
          </h3>
          <div style={{ 
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            {interview.questions?.map((q, index) => {
              const question = q.questionId || q;
              return (
                <div 
                  key={index}
                  style={{ 
                    padding: '16px 20px',
                    borderBottom: index < interview.questions.length - 1 ? '1px solid #e2e8f0' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ 
                      minWidth: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#eef2ff',
                      color: '#667eea',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '14px'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#2d3748', fontSize: '14px', lineHeight: 1.6 }}>
                        {question.question || 'Question not available'}
                      </div>
                      {question.questionType && (
                        <div style={{ marginTop: '8px' }}>
                          <span style={{ 
                            fontSize: '11px',
                            padding: '4px 8px',
                            background: '#e7f3ff',
                            color: '#0066cc',
                            borderRadius: '4px',
                            fontWeight: 600
                          }}>
                            {question.questionType}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* My Submissions */}
        {submissions.length > 0 && (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '20px', fontWeight: 700, marginBottom: '16px' }}>
              My Submissions ({submissions.length})
            </h3>

            {/* Submission Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginBottom: '20px',
              borderBottom: '2px solid #e2e8f0',
              overflowX: 'auto'
            }}>
              {submissions.map((submission, index) => {
                const hasScore = submission.overallFeedback?.overallScore !== undefined && 
                                 typeof submission.overallFeedback.overallScore === 'number';
                const score = hasScore ? Math.round(submission.overallFeedback.overallScore) : null;
                const scoreColor = score >= 70 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
                
                return (
                  <button
                    key={submission._id}
                    onClick={() => setSelectedSubmission(submission)}
                    style={{
                      padding: '10px 16px',
                      background: selectedSubmission?._id === submission._id ? '#667eea' : 'white',
                      color: selectedSubmission?._id === submission._id ? 'white' : '#718096',
                      border: 'none',
                      borderBottom: `3px solid ${selectedSubmission?._id === submission._id ? '#667eea' : 'transparent'}`,
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span>{index === 0 ? '📋 Latest' : `Attempt ${submissions.length - index}`}</span>
                    {hasScore && (
                      <span style={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: selectedSubmission?._id === submission._id ? 'white' : scoreColor
                      }}>
                        {score}/100
                      </span>
                    )}
                    {!hasScore && (
                      <span style={{
                        fontSize: '11px',
                        opacity: 0.7,
                        fontStyle: 'italic'
                      }}>
                        No score
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected Submission Details */}
            {selectedSubmission && (
              <div style={{ 
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '20px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  paddingBottom: '16px',
                  borderBottom: '2px solid #e2e8f0',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
                        SUBMITTED ON
                      </div>
                      <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
                        {new Date(selectedSubmission.submittedAt).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
                        TIME TAKEN
                      </div>
                      <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 600 }}>
                        {formatDuration(selectedSubmission.startedAt, selectedSubmission.submittedAt)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: selectedSubmission.status === 'submitted' ? '#c6f6d5' : '#fed7d7',
                      color: selectedSubmission.status === 'submitted' ? '#22543d' : '#742a2a'
                    }}>
                      {selectedSubmission.status === 'submitted' ? 'Submitted' : 'Timed Out'}
                    </div>
                  </div>
                </div>

                {/* Video Recording */}
                {selectedSubmission.videoRecording && (isAdmin || interview.allowStudentDownload || interview.requireVideoRecording) && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ fontSize: '16px', color: '#667eea', margin: 0, fontWeight: 700 }}>
                        📹 Interview Recording
                      </h4>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {isAdmin && (
                          <button
                            onClick={handleRegenerateAI}
                            disabled={regeneratingAI}
                            style={{
                              padding: '8px 16px',
                              background: regeneratingAI ? '#cbd5e0' : '#9f7aea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: regeneratingAI ? 'not-allowed' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              opacity: regeneratingAI ? 0.6 : 1
                            }}
                          >
                            🤖 {regeneratingAI ? 'Regenerating...' : 'Regenerate AI'}
                          </button>
                        )}
                        {interview.allowStudentDownload && (
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${BASE_URL}/api/interview/${interview._id}/download-video`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Download failed');
                                }
                                
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `interview-${interview.title.replace(/\s+/g, '-')}-${new Date(selectedSubmission.submittedAt).toISOString().split('T')[0]}.webm`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (error) {
                                console.error('Download error:', error);
                                alert('Failed to download video');
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            📥 Download
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: selectedSubmission.videoChapters && selectedSubmission.videoChapters.length > 0 ? '1fr 280px' : '1fr', gap: '16px' }}>
                      {/* Video Player */}
                      <div>
                        {loadingVideo ? (
                          <div style={{
                            width: '100%',
                            height: '400px',
                            borderRadius: '8px',
                            background: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            Loading video...
                          </div>
                        ) : videoUrl ? (
                          <video
                            ref={videoRef}
                            controls
                            onTimeUpdate={handleVideoTimeUpdate}
                            key={videoUrl}
                            style={{
                              width: '100%',
                              maxHeight: '400px',
                              borderRadius: '8px',
                              background: '#000'
                            }}
                          >
                            <source src={videoUrl} type="video/webm" />
                            <source src={videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '400px',
                            borderRadius: '8px',
                            background: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            Video not available
                          </div>
                        )}
                      </div>
                      
                      {/* Video Chapters Sidebar */}
                      {selectedSubmission.videoChapters && selectedSubmission.videoChapters.length > 0 && (
                        <div style={{ 
                          background: 'white', 
                          borderRadius: '8px', 
                          border: '1px solid #e2e8f0',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          scrollBehavior: 'smooth'
                        }}
                        className="chapters-sidebar">
                          <style>{`
                            .chapters-sidebar::-webkit-scrollbar {
                              width: 8px;
                            }
                            .chapters-sidebar::-webkit-scrollbar-track {
                              background: #f1f1f1;
                              border-radius: 4px;
                            }
                            .chapters-sidebar::-webkit-scrollbar-thumb {
                              background: #cbd5e0;
                              border-radius: 4px;
                            }
                            .chapters-sidebar::-webkit-scrollbar-thumb:hover {
                              background: #a0aec0;
                            }
                          `}</style>
                          <div style={{ 
                            padding: '12px 16px', 
                            borderBottom: '2px solid #e2e8f0',
                            position: 'sticky',
                            top: 0,
                            background: 'white',
                            zIndex: 1
                          }}>
                            <h5 style={{ margin: 0, color: '#2d3748', fontSize: '14px', fontWeight: 600 }}>
                              📑 Chapters ({selectedSubmission.videoChapters.length})
                            </h5>
                          </div>
                          <div>
                            {selectedSubmission.videoChapters.map((chapter, index) => (
                              <div
                                key={index}
                                onClick={() => handleChapterClick(index)}
                                style={{
                                  padding: '12px 16px',
                                  borderBottom: index < selectedSubmission.videoChapters.length - 1 ? '1px solid #e2e8f0' : 'none',
                                  cursor: 'pointer',
                                  background: currentChapter === index ? '#f0f4ff' : 'white',
                                  borderLeft: currentChapter === index ? '3px solid #667eea' : '3px solid transparent',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  if (currentChapter !== index) {
                                    e.currentTarget.style.background = '#f7fafc';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (currentChapter !== index) {
                                    e.currentTarget.style.background = 'white';
                                  }
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    fontWeight: 600, 
                                    color: currentChapter === index ? '#667eea' : '#718096',
                                    minWidth: '45px',
                                    fontFamily: 'monospace'
                                  }}>
                                    {formatChapterTime(chapter.timestamp)}
                                  </span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ 
                                      fontSize: '11px', 
                                      fontWeight: 600,
                                      color: chapter.isFollowUp ? '#9f7aea' : '#667eea',
                                      marginBottom: '4px'
                                    }}>
                                      {chapter.isFollowUp 
                                        ? `Q${chapter.parentQuestionNumber} Follow-up` 
                                        : `Question ${chapter.questionNumber}`}
                                    </div>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      color: '#2d3748',
                                      lineHeight: 1.4,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical'
                                    }}>
                                      {chapter.questionText}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Missing Score Warning */}
                {selectedSubmission && (!selectedSubmission.overallFeedback || 
                  selectedSubmission.overallFeedback.overallScore === undefined || 
                  typeof selectedSubmission.overallFeedback.overallScore !== 'number') && (
                  <div style={{ 
                    marginBottom: '24px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      color: 'white'
                    }}>
                      <span style={{ fontSize: '24px' }}>ℹ️</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 700 }}>
                          Overall Score Not Available
                        </h4>
                        <p style={{ margin: '0 0 12px 0', fontSize: '14px', lineHeight: 1.6, opacity: 0.95 }}>
                          This interview attempt doesn't have an overall performance score yet. 
                          {isAdmin ? ' Click the button below to regenerate AI assessment and generate the score.' : ' Please contact your instructor to generate the score for this attempt.'}
                        </p>
                        {isAdmin && (
                          <button
                            onClick={handleRegenerateAI}
                            disabled={regeneratingAI}
                            style={{
                              padding: '10px 18px',
                              background: regeneratingAI ? 'rgba(255, 255, 255, 0.3)' : 'white',
                              color: regeneratingAI ? 'white' : '#d97706',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: 600,
                              cursor: regeneratingAI ? 'not-allowed' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              opacity: regeneratingAI ? 0.7 : 1,
                              transition: 'all 0.2s'
                            }}
                          >
                            🤖 {regeneratingAI ? 'Regenerating...' : 'Regenerate AI Assessment'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Overall Interview Feedback */}
                {selectedSubmission?.overallFeedback && selectedSubmission.overallFeedback.overallScore !== undefined && typeof selectedSubmission.overallFeedback.overallScore === 'number' && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                      borderRadius: '12px', 
                      padding: '24px',
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)'
                    }}>
                      <div style={{ background: 'white', borderRadius: '10px', padding: '20px' }}>
                        <div style={{ 
                          marginBottom: '20px',
                          paddingBottom: '16px',
                          borderBottom: '2px solid #e2e8f0'
                        }}>
                          <h4 style={{ color: '#667eea', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '24px' }}>🎯</span> Overall Interview Performance
                          </h4>
                        </div>

                        {/* Student View - Personalized Feedback */}
                        {selectedSubmission.overallFeedback.studentSummary && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ 
                              display: 'block', 
                              fontSize: '14px', 
                              color: '#667eea', 
                              marginBottom: '8px', 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.5px' 
                            }}>
                              📋 How You Performed
                            </strong>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '15px', 
                              color: '#2d3748', 
                              lineHeight: 1.7, 
                              padding: '12px', 
                              background: '#f7fafc', 
                              borderRadius: '8px', 
                              borderLeft: '3px solid #667eea' 
                            }}>
                              {selectedSubmission.overallFeedback.studentSummary}
                            </p>
                          </div>
                        )}

                        {selectedSubmission.overallFeedback.studentStrengths && selectedSubmission.overallFeedback.studentStrengths.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ 
                              display: 'block', 
                              fontSize: '14px', 
                              color: '#16a34a', 
                              marginBottom: '8px', 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.5px' 
                            }}>
                              ✓ Your Strengths
                            </strong>
                            <ul style={{ 
                              margin: 0, 
                              paddingLeft: '24px', 
                              fontSize: '15px', 
                              color: '#15803d', 
                              background: '#f0fdf4', 
                              padding: '16px 16px 16px 40px', 
                              borderRadius: '8px', 
                              border: '2px solid #86efac' 
                            }}>
                              {selectedSubmission.overallFeedback.studentStrengths.map((strength, idx) => (
                                <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>{strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {selectedSubmission.overallFeedback.studentImprovements && selectedSubmission.overallFeedback.studentImprovements.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ 
                              display: 'block', 
                              fontSize: '14px', 
                              color: '#d97706', 
                              marginBottom: '8px', 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.5px' 
                            }}>
                              💡 Areas to Focus On
                            </strong>
                            <ul style={{ 
                              margin: 0, 
                              paddingLeft: '24px', 
                              fontSize: '15px', 
                              color: '#92400e', 
                              background: '#fffbeb', 
                              padding: '16px 16px 16px 40px', 
                              borderRadius: '8px', 
                              border: '2px solid #fcd34d' 
                            }}>
                              {selectedSubmission.overallFeedback.studentImprovements.map((improvement, idx) => (
                                <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>{improvement}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {selectedSubmission.overallFeedback.missedTerminology && selectedSubmission.overallFeedback.missedTerminology.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <strong style={{ 
                              display: 'block', 
                              fontSize: '14px', 
                              color: '#dc2626', 
                              marginBottom: '8px', 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.5px' 
                            }}>
                              🎓 Important Terms to Learn
                            </strong>
                            <div style={{ 
                              background: '#fef2f2', 
                              padding: '16px', 
                              borderRadius: '8px', 
                              border: '2px solid #fca5a5' 
                            }}>
                              <p style={{ 
                                margin: '0 0 8px 0', 
                                fontSize: '13px', 
                                color: '#991b1b', 
                                fontStyle: 'italic' 
                              }}>
                                These terms would have strengthened your answers:
                              </p>
                              <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '15px', color: '#991b1b' }}>
                                {selectedSubmission.overallFeedback.missedTerminology.map((term, idx) => (
                                  <li key={idx} style={{ marginBottom: '6px', lineHeight: 1.6 }}>
                                    <strong>{term}</strong>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {selectedSubmission.overallFeedback.studentFeedforward && (
                          <div style={{ marginBottom: '16px' }}>
                            <strong style={{ 
                              display: 'block', 
                              fontSize: '14px', 
                              color: '#7c3aed', 
                              marginBottom: '8px', 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.5px' 
                            }}>
                              🎯 Your Next Steps
                            </strong>
                            <div style={{ 
                              padding: '16px', 
                              background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', 
                              borderRadius: '8px', 
                              border: '2px solid #c084fc',
                              fontSize: '15px',
                              color: '#5b21b6',
                              lineHeight: 1.7
                            }}>
                              {selectedSubmission.overallFeedback.studentFeedforward}
                            </div>
                          </div>
                        )}

                        {isAdmin && selectedSubmission.overallFeedback.generatedAt && (
                          <div style={{ 
                            marginTop: '16px', 
                            paddingTop: '12px', 
                            borderTop: '1px solid #e2e8f0', 
                            textAlign: 'center' 
                          }}>
                            <span style={{ fontSize: '11px', color: '#718096' }}>
                              Generated {new Date(selectedSubmission.overallFeedback.generatedAt).toLocaleString()}
                              {selectedSubmission.overallFeedback.aiModelUsed && ` • AI: ${selectedSubmission.overallFeedback.aiModelUsed === 'gemini' ? 'Google Gemini' : 'Deepseek AI'}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Answers */}
                <div>
                  <h4 style={{ fontSize: '16px', color: '#2d3748', margin: '0 0 16px 0', fontWeight: 700 }}>
                    My Answers
                  </h4>
                  {interview.questions?.map((q, index) => {
                    if (!q) return null;
                    const question = q.questionId || q;
                    if (!question) return null;
                    const answer = getAnswerForQuestion(selectedSubmission, question);
                    
                    // Get follow-ups for this question
                    // Handle multiple ID formats to ensure we match correctly
                    const questionId = question._id || question.questionId || question;
                    const questionIdStr = typeof questionId === 'object' ? questionId._id || questionId.toString() : questionId.toString();
                    
                    const followUps = selectedSubmission.responses?.filter(r => {
                      if (!r.isFollowUp) return false;
                      const parentId = r.parentQuestionId?._id || r.parentQuestionId;
                      const parentIdStr = typeof parentId === 'object' ? parentId._id || parentId.toString() : parentId?.toString();
                      // Match by string comparison to handle different object references
                      return parentIdStr === questionIdStr;
                    }) || [];
                    
                    // Debug logging for coding questions
                    if (question.questionType === 'Coding') {
                      console.log(`Question ${index + 1} (${question.questionType}):`, {
                        questionId: questionIdStr,
                        hasAnswer: !!answer,
                        answerHasCode: !!answer?.code,
                        followUpsCount: followUps.length,
                        followUps: followUps.map(f => ({
                          question: f.followUpQuestion?.substring(0, 50) + '...',
                          hasCode: !!f.code,
                          hasAssessment: !!f.aiAssessment,
                          parentId: typeof f.parentQuestionId === 'object' ? f.parentQuestionId?._id : f.parentQuestionId
                        }))
                      });
                    }
                    
                    // Debug logging for all follow-ups with code
                    followUps.forEach((fu, idx) => {
                      if (fu.code) {
                        console.log(`Follow-up ${idx + 1} has code:`, {
                          question: fu.followUpQuestion,
                          codeLength: fu.code.length,
                          language: fu.selectedLanguage,
                          hasAnswer: !!fu.answer
                        });
                      }
                    });
                    
                    return (
                      <div 
                        key={index}
                        style={{ 
                          background: '#f7fafc',
                          padding: '16px',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        {/* Main Question */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ 
                            display: 'inline-block',
                            padding: '4px 10px', 
                            borderRadius: '12px', 
                            fontSize: '11px', 
                            fontWeight: 700, 
                            background: '#667eea', 
                            color: 'white',
                            marginBottom: '8px'
                          }}>
                            Question {index + 1}
                          </div>
                          <strong style={{ display: 'block', color: '#2d3748', fontSize: '14px' }}>
                            {question?.question || 'Question not available'}
                          </strong>
                        </div>
                        
                        {answer ? (
                          <div>
                            {/* Display cleaned answer if available, otherwise show original */}
                            <div style={{ 
                              background: answer.cleanedAnswer ? 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' : 'white',
                              padding: '12px',
                              borderRadius: '6px',
                              fontSize: answer.cleanedAnswer ? '15px' : '13px',
                              color: '#2d3748',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                              border: answer.cleanedAnswer ? '1px solid #667eea30' : 'none'
                            }}>
                              {answer.cleanedAnswer || answer.answer || 'No answer provided'}
                            </div>
                            {answer.cleanedAnswer && (
                              <div style={{ fontSize: '10px', color: '#667eea', marginTop: '4px', fontStyle: 'italic' }}>
                                🤖 AI-cleaned transcript
                              </div>
                            )}

                            {/* Display submitted code if available */}
                            {answer.code && (
                              <div style={{ marginTop: '16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: '8px'
                                }}>
                                  <strong style={{ fontSize: '14px', color: '#2d3748' }}>
                                    💻 Submitted Code
                                  </strong>
                                  {answer.selectedLanguage && (
                                    <span style={{
                                      fontSize: '12px',
                                      padding: '4px 12px',
                                      background: '#667eea',
                                      color: 'white',
                                      borderRadius: '12px',
                                      fontWeight: 600,
                                      textTransform: 'uppercase'
                                    }}>
                                      {answer.selectedLanguage}
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                  <Editor
                                    height="400px"
                                    language={answer.selectedLanguage === 'csharp' ? 'csharp' : answer.selectedLanguage}
                                    theme="vs-dark"
                                    value={answer.code}
                                    options={{
                                      readOnly: true,
                                      minimap: { enabled: true },
                                      scrollBeyondLastLine: false,
                                      fontSize: 14,
                                      lineNumbers: 'on',
                                      renderLineHighlight: 'none',
                                      contextmenu: false,
                                      wordWrap: 'on',
                                      folding: true
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* AI Assessment */}
                            {interview.showAIEvaluation && answer.aiAssessment && (
                              <div style={{ 
                                marginTop: '12px',
                                background: '#f0fdf4',
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #86efac'
                              }}>
                                {isAdmin && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <strong style={{ color: '#166534', fontSize: '14px' }}>🎯 SCORE</strong>
                                    <span style={{ 
                                      fontSize: '24px',
                                      fontWeight: 700,
                                      color: answer.aiAssessment.score >= 70 ? '#16a34a' : answer.aiAssessment.score >= 50 ? '#ca8a04' : '#dc2626'
                                    }}>
                                      {answer.aiAssessment.score}/100
                                    </span>
                                  </div>
                                )}
                                
                                {((isAdmin && answer.aiAssessment.adminFeedback) || (!isAdmin && answer.aiAssessment.feedback)) && (
                                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#15803d', lineHeight: 1.5 }}>
                                    {isAdmin ? answer.aiAssessment.adminFeedback : answer.aiAssessment.feedback}
                                  </p>
                                )}

                                {((isAdmin && answer.aiAssessment.adminStrengths) || (!isAdmin && answer.aiAssessment.strengths)) && 
                                 (isAdmin ? answer.aiAssessment.adminStrengths : answer.aiAssessment.strengths).length > 0 && (
                                  <div style={{ marginBottom: '8px' }}>
                                    <strong style={{ fontSize: '14px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                                      ✓ Strengths:
                                    </strong>
                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
                                      {(isAdmin ? answer.aiAssessment.adminStrengths : answer.aiAssessment.strengths).map((strength, idx) => (
                                        <li key={idx}>{strength}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {((isAdmin && answer.aiAssessment.adminImprovements) || (!isAdmin && answer.aiAssessment.improvements)) &&
                                 (isAdmin ? answer.aiAssessment.adminImprovements : answer.aiAssessment.improvements).length > 0 && (
                                  <div>
                                    <strong style={{ fontSize: '14px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                                      💡 Areas to Improve:
                                    </strong>
                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
                                      {(isAdmin ? answer.aiAssessment.adminImprovements : answer.aiAssessment.improvements).map((improvement, idx) => (
                                        <li key={idx}>{improvement}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {isAdmin && answer.aiAssessment.aiModelUsed && (
                                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #86efac' }}>
                                    <span style={{ fontSize: '11px', color: '#15803d', fontWeight: 600 }}>
                                      🤖 AI Model: {answer.aiAssessment.aiModelUsed === 'gemini' ? 'Google Gemini' : 'Deepseek AI'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {answer.timeSpent && (
                              <div style={{ marginTop: '8px', fontSize: '12px', color: '#718096' }}>
                                ⏱️ Time spent: {Math.round(answer.timeSpent / 1000)} seconds
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ 
                            padding: '12px',
                            background: '#fff5f5',
                            borderRadius: '6px',
                            color: '#c53030',
                            fontSize: '13px'
                          }}>
                            No answer provided
                          </div>
                        )}

                        {/* Follow-up Questions for this main question */}
                        {followUps.length > 0 && (
                          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px dashed #d6bcfa' }}>
                            {followUps.map((followUp, fuIdx) => (
                              <div 
                                key={fuIdx}
                                style={{ 
                                  background: '#faf5ff',
                                  padding: '12px',
                                  borderRadius: '6px',
                                  marginBottom: fuIdx < followUps.length - 1 ? '12px' : '0',
                                  border: '2px solid #d6bcfa'
                                }}
                              >
                                <div style={{ marginBottom: '8px' }}>
                                  <span style={{ 
                                    display: 'inline-block',
                                    padding: '4px 10px', 
                                    borderRadius: '12px', 
                                    fontSize: '11px', 
                                    fontWeight: 700, 
                                    background: '#9f7aea', 
                                    color: 'white',
                                    marginBottom: '6px'
                                  }}>
                                    🤖 Q{index + 1} Follow-up
                                  </span>
                                  <strong style={{ display: 'block', color: '#44337a', fontSize: '14px' }}>
                                    {followUp.followUpQuestion}
                                  </strong>
                                </div>

                                {/* Display cleaned answer if available, otherwise show original */}
                                <div style={{ 
                                  background: followUp.cleanedAnswer ? 'linear-gradient(135deg, #9f7aea15 0%, #d6bcfa15 100%)' : 'white',
                                  padding: '12px',
                                  borderRadius: '6px',
                                  fontSize: followUp.cleanedAnswer ? '15px' : '13px',
                                  color: '#2d3748',
                                  lineHeight: 1.6,
                                  whiteSpace: 'pre-wrap',
                                  border: followUp.cleanedAnswer ? '1px solid #9f7aea30' : '1px solid #e9d8fd'
                                }}>
                                  {followUp.cleanedAnswer || followUp.answer || 'No answer provided'}
                                </div>
                                {followUp.cleanedAnswer && (
                                  <div style={{ fontSize: '10px', color: '#9f7aea', marginTop: '4px', fontStyle: 'italic' }}>
                                    🤖 AI-cleaned transcript
                                  </div>
                                )}

                                {/* Display submitted code for follow-up if available */}
                                {followUp.code && (
                                  <div style={{ marginTop: '16px' }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      marginBottom: '8px'
                                    }}>
                                      <strong style={{ fontSize: '14px', color: '#44337a' }}>
                                        💻 Submitted Code
                                      </strong>
                                      {followUp.selectedLanguage && (
                                        <span style={{
                                          fontSize: '12px',
                                          padding: '4px 12px',
                                          background: '#9f7aea',
                                          color: 'white',
                                          borderRadius: '12px',
                                          fontWeight: 600,
                                          textTransform: 'uppercase'
                                        }}>
                                          {followUp.selectedLanguage}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{
                                      border: '1px solid #e9d8fd',
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <Editor
                                        height="400px"
                                        language={followUp.selectedLanguage === 'csharp' ? 'csharp' : followUp.selectedLanguage}
                                        theme="vs-dark"
                                        value={followUp.code}
                                        options={{
                                          readOnly: true,
                                          minimap: { enabled: true },
                                          scrollBeyondLastLine: false,
                                          fontSize: 14,
                                          lineNumbers: 'on',
                                          renderLineHighlight: 'none',
                                          contextmenu: false,
                                          wordWrap: 'on',
                                          folding: true
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* AI Assessment for follow-up */}
                                {interview.showAIEvaluation && followUp.aiAssessment && (
                                  <div style={{ 
                                    marginTop: '12px',
                                    background: '#f0fdf4',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: '1px solid #86efac'
                                  }}>
                                    {isAdmin && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <strong style={{ color: '#166534', fontSize: '12px' }}>🎯 SCORE</strong>
                                        <span style={{ 
                                          fontSize: '24px',
                                          fontWeight: 700,
                                          color: followUp.aiAssessment.score >= 70 ? '#16a34a' : followUp.aiAssessment.score >= 50 ? '#ca8a04' : '#dc2626'
                                        }}>
                                          {followUp.aiAssessment.score}/100
                                        </span>
                                      </div>
                                    )}
                                    
                                    {((isAdmin && followUp.aiAssessment.adminFeedback) || (!isAdmin && followUp.aiAssessment.feedback)) && (
                                      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#15803d', lineHeight: 1.5 }}>
                                        {isAdmin ? followUp.aiAssessment.adminFeedback : followUp.aiAssessment.feedback}
                                      </p>
                                    )}

                                    {((isAdmin && followUp.aiAssessment.adminStrengths) || (!isAdmin && followUp.aiAssessment.strengths)) &&
                                     (isAdmin ? followUp.aiAssessment.adminStrengths : followUp.aiAssessment.strengths).length > 0 && (
                                      <div style={{ marginBottom: '8px' }}>
                                        <strong style={{ fontSize: '14px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                                          ✓ Strengths:
                                        </strong>
                                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
                                          {(isAdmin ? followUp.aiAssessment.adminStrengths : followUp.aiAssessment.strengths).map((strength, idx) => (
                                            <li key={idx}>{strength}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {((isAdmin && followUp.aiAssessment.adminImprovements) || (!isAdmin && followUp.aiAssessment.improvements)) &&
                                     (isAdmin ? followUp.aiAssessment.adminImprovements : followUp.aiAssessment.improvements).length > 0 && (
                                      <div>
                                        <strong style={{ fontSize: '14px', color: '#166534', display: 'block', marginBottom: '4px' }}>
                                          💡 Areas to Improve:
                                        </strong>
                                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
                                          {(isAdmin ? followUp.aiAssessment.adminImprovements : followUp.aiAssessment.improvements).map((improvement, idx) => (
                                            <li key={idx}>{improvement}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {isAdmin && followUp.aiAssessment.aiModelUsed && (
                                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #c4b5fd' }}>
                                        <span style={{ fontSize: '11px', color: '#5b21b6', fontWeight: 600 }}>
                                          🤖 AI Model: {followUp.aiAssessment.aiModelUsed === 'gemini' ? 'Google Gemini' : 'Deepseek AI'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {followUp.timeSpent && (
                                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b46c1' }}>
                                    ⏱️ Time spent: {Math.round(followUp.timeSpent / 1000)} seconds
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default InterviewDetails;
